import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatCurrency, formatDate } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Trash2, Wallet, FileText, Download } from 'lucide-react';
import ExportButton from '../components/ExportButton';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const expenseCategories = [
  { en: 'Rent', ar: 'إيجار' },
  { en: 'Utilities', ar: 'خدمات' },
  { en: 'Supplies', ar: 'مستلزمات' },
  { en: 'Equipment', ar: 'معدات' },
  { en: 'Marketing', ar: 'تسويق' },
  { en: 'Transportation', ar: 'نقل' },
  { en: 'Maintenance', ar: 'صيانة' },
  { en: 'Other', ar: 'أخرى' },
];

export default function ExpensesPage() {
  const { t, language } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    category: 'all'
  });
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    payment_method: 'cash',
    date: new Date().toISOString().split('T')[0],
  });

  // Company Information
  const companyInfo = {
    name: 'Qassioun Distribution',
    phone: '07496892537',
    email: 'Ameer266668@gmail.com',
    address: '3 South Way, Wembley HA9 0HF, UK'
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/expenses`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      fetchExpenses();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm'))) {
      try {
        await axios.delete(`${API}/expenses/${id}`);
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      category: '',
      description: '',
      amount: '',
      payment_method: 'cash',
      date: new Date().toISOString().split('T')[0],
    });
  };

  // Generate PDF Report
  const generateExpensesReport = () => {
    // Filter expenses by date and category using string comparison (avoids timezone issues)
    let filteredForReport = expenses.filter(exp => {
      const expDateStr = exp.date.split('T')[0];
      return expDateStr >= reportData.start_date && expDateStr <= reportData.end_date;
    });
    
    if (reportData.category !== 'all') {
      filteredForReport = filteredForReport.filter(exp => exp.category === reportData.category);
    }
    
    // Sort by date
    filteredForReport.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calculate totals by category
    const categoryTotals = {};
    let grandTotal = 0;
    let cashTotal = 0;
    let bankTotal = 0;
    
    filteredForReport.forEach(exp => {
      categoryTotals[exp.category] = (categoryTotals[exp.category] || 0) + exp.amount;
      grandTotal += exp.amount;
      if (exp.payment_method === 'cash') {
        cashTotal += exp.amount;
      } else {
        bankTotal += exp.amount;
      }
    });
    
    // Create PDF
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(194, 24, 91); // Primary color
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo.name, pageWidth / 2, 18, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(companyInfo.address, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Tel: ${companyInfo.phone} | Email: ${companyInfo.email}`, pageWidth / 2, 35, { align: 'center' });
    
    // Title
    doc.setTextColor(51, 51, 51);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('EXPENSES REPORT', pageWidth / 2, 58, { align: 'center' });
    
    // Date range
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const dateRange = `${startDate.toLocaleDateString('en-GB')} - ${endDate.toLocaleDateString('en-GB')}`;
    doc.text(`Period: ${dateRange}`, pageWidth / 2, 67, { align: 'center' });
    
    if (reportData.category !== 'all') {
      doc.text(`Category: ${reportData.category}`, pageWidth / 2, 74, { align: 'center' });
    }
    
    // Summary boxes
    let yPos = 85;
    
    // Draw summary boxes
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(15, yPos, 55, 25, 3, 3, 'F');
    doc.roundedRect(77, yPos, 55, 25, 3, 3, 'F');
    doc.roundedRect(139, yPos, 55, 25, 3, 3, 'F');
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Total Expenses', 42.5, yPos + 8, { align: 'center' });
    doc.text('Cash', 104.5, yPos + 8, { align: 'center' });
    doc.text('Bank', 166.5, yPos + 8, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(194, 24, 91);
    doc.text(`£${grandTotal.toFixed(2)}`, 42.5, yPos + 19, { align: 'center' });
    doc.setTextColor(34, 139, 34);
    doc.text(`£${cashTotal.toFixed(2)}`, 104.5, yPos + 19, { align: 'center' });
    doc.setTextColor(30, 144, 255);
    doc.text(`£${bankTotal.toFixed(2)}`, 166.5, yPos + 19, { align: 'center' });
    
    yPos += 35;
    
    // Table header
    doc.setFillColor(194, 24, 91);
    doc.rect(15, yPos, pageWidth - 30, 10, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('#', 20, yPos + 7);
    doc.text('Date', 30, yPos + 7);
    doc.text('Category', 60, yPos + 7);
    doc.text('Description', 95, yPos + 7);
    doc.text('Method', 145, yPos + 7);
    doc.text('Amount', 175, yPos + 7);
    
    yPos += 12;
    
    // Table rows
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 51, 51);
    doc.setFontSize(9);
    
    filteredForReport.forEach((exp, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Alternate row colors
      if (index % 2 === 0) {
        doc.setFillColor(249, 249, 249);
        doc.rect(15, yPos - 4, pageWidth - 30, 8, 'F');
      }
      
      doc.setTextColor(51, 51, 51);
      doc.text(`${index + 1}`, 20, yPos);
      doc.text(new Date(exp.date).toLocaleDateString('en-GB'), 30, yPos);
      doc.text(exp.category.substring(0, 15), 60, yPos);
      doc.text(exp.description.substring(0, 25), 95, yPos);
      
      // Payment method with color
      if (exp.payment_method === 'cash') {
        doc.setTextColor(34, 139, 34);
        doc.text('Cash', 145, yPos);
      } else {
        doc.setTextColor(30, 144, 255);
        doc.text('Bank', 145, yPos);
      }
      
      doc.setTextColor(194, 24, 91);
      doc.setFont('helvetica', 'bold');
      doc.text(`£${exp.amount.toFixed(2)}`, 175, yPos);
      doc.setFont('helvetica', 'normal');
      
      yPos += 8;
    });
    
    // Category breakdown
    yPos += 10;
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 51, 51);
    doc.text('Breakdown by Category', 15, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).forEach(([cat, total]) => {
      doc.setTextColor(100, 100, 100);
      doc.text(cat, 20, yPos);
      doc.setTextColor(194, 24, 91);
      doc.setFont('helvetica', 'bold');
      doc.text(`£${total.toFixed(2)}`, 80, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 6;
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated: ${new Date().toLocaleString('en-GB')} | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save
    const fileName = `Expenses_Report_${reportData.start_date}_to_${reportData.end_date}.pdf`;
    doc.save(fileName);
    setReportDialogOpen(false);
  };

  const filteredExpenses = expenses.filter((e) => {
    const matchesSearch =
      e.category.toLowerCase().includes(search.toLowerCase()) ||
      e.description.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (periodFilter === 'all') return true;
    const expDate = new Date(e.date);
    const now = new Date();
    if (periodFilter === 'today') {
      return expDate.toISOString().split('T')[0] === now.toISOString().split('T')[0];
    }
    if (periodFilter === 'week') {
      // Business week: Sunday → Friday (Saturday excluded)
      const weekStart = new Date(now);
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // back to Sunday (getDay: Sun=0)
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 5); // Friday
      weekEnd.setHours(23, 59, 59, 999);
      return expDate >= weekStart && expDate <= weekEnd;
    }
    if (periodFilter === 'month') {
      return expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear();
    }
    return true;
  });

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="expenses-title">{t('expenses')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'تتبع المصروفات' : 'Track your expenses'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReportDialogOpen(true)} data-testid="expense-report-btn">
            <FileText size={18} className="me-2" />
            {language === 'ar' ? 'تقرير PDF' : 'PDF Report'}
          </Button>
          <ExportButton
            endpoint="expenses"
            filename="expenses"
            label={language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="add-expense-btn">
            <Plus size={18} className="me-2" />
            {t('addExpense')}
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="border-border/40 bg-gradient-to-r from-orange-50 to-red-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? 'إجمالي المصروفات' : 'Total Expenses'}
              </p>
              <p className="text-3xl font-bold text-orange-600" data-testid="total-expenses">
                {formatCurrency(totalExpenses)}
              </p>
            </div>
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
              <Wallet className="text-orange-600" size={28} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search & Filter */}
      <Card className="border-border/40">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10"
              data-testid="expenses-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: language === 'ar' ? 'الكل' : 'All' },
              { key: 'today', label: language === 'ar' ? 'اليوم' : 'Today' },
              { key: 'week', label: language === 'ar' ? 'هذا الأسبوع' : 'This Week' },
              { key: 'month', label: language === 'ar' ? 'هذا الشهر' : 'This Month' },
            ].map((f) => (
              <Button
                key={f.key}
                variant={periodFilter === f.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodFilter(f.key)}
                className="rounded-full px-4 text-xs"
                data-testid={`expense-filter-${f.key}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('date')}</TableHead>
                    <TableHead>{t('category')}</TableHead>
                    <TableHead>{t('description')}</TableHead>
                    <TableHead>{t('amount')}</TableHead>
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                      <TableCell>{formatDate(expense.date)}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800 text-xs">
                          {language === 'ar'
                            ? expenseCategories.find((c) => c.en === expense.category)?.ar || expense.category
                            : expense.category}
                        </span>
                      </TableCell>
                      <TableCell>{expense.description}</TableCell>
                      <TableCell className="font-bold text-orange-600">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(expense.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-expense-${expense.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('addExpense')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('category')}</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder={language === 'ar' ? 'اختر الفئة' : 'Select category'} />
                </SelectTrigger>
                <SelectContent>
                  {expenseCategories.map((cat) => (
                    <SelectItem key={cat.en} value={cat.en}>
                      {language === 'ar' ? cat.ar : cat.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={2}
                data-testid="expense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('amount')} (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('date')}</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
              >
                <SelectTrigger data-testid="expense-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{language === 'ar' ? '💵 كاش' : '💵 Cash'}</SelectItem>
                  <SelectItem value="bank">{language === 'ar' ? '🏦 بنك' : '🏦 Bank'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" data-testid="expense-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PDF Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {language === 'ar' ? 'تقرير المصروفات PDF' : 'Expenses PDF Report'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'من تاريخ' : 'From Date'}</Label>
                <Input
                  type="date"
                  value={reportData.start_date}
                  onChange={(e) => setReportData({ ...reportData, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'إلى تاريخ' : 'To Date'}</Label>
                <Input
                  type="date"
                  value={reportData.end_date}
                  onChange={(e) => setReportData({ ...reportData, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الفئة' : 'Category'}</Label>
              <Select
                value={reportData.category}
                onValueChange={(value) => setReportData({ ...reportData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'ar' ? 'جميع الفئات' : 'All Categories'}</SelectItem>
                  {expenseCategories.map((cat) => (
                    <SelectItem key={cat.en} value={cat.en}>
                      {language === 'ar' ? cat.ar : cat.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview info */}
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">
                {language === 'ar' ? 'معاينة:' : 'Preview:'}
              </p>
              <div className="flex justify-between items-center">
                <span className="text-sm">
                  {expenses.filter(exp => {
                    const expDateStr = exp.date.split('T')[0];
                    const dateMatch = expDateStr >= reportData.start_date && expDateStr <= reportData.end_date;
                    const catMatch = reportData.category === 'all' || exp.category === reportData.category;
                    return dateMatch && catMatch;
                  }).length} {language === 'ar' ? 'مصروف' : 'expenses'}
                </span>
                <Badge variant="secondary">
                  {formatCurrency(
                    expenses.filter(exp => {
                      const expDateStr = exp.date.split('T')[0];
                      const dateMatch = expDateStr >= reportData.start_date && expDateStr <= reportData.end_date;
                      const catMatch = reportData.category === 'all' || exp.category === reportData.category;
                      return dateMatch && catMatch;
                    }).reduce((sum, e) => sum + e.amount, 0)
                  )}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={generateExpensesReport}>
              <Download className="h-4 w-4 me-2" />
              {language === 'ar' ? 'تحميل PDF' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
